/**
 * T-0770 (`docs/concept-mobile-app.md`, LC-6b): the GraphQL field sets a work item is read with, in
 * a module of their own. The realtime subscriptions the launch opens ask them
 * (`workItemSubscriptions.ts`), and so does `WorkItemApi`; the launch entry carries these strings
 * and not the rest of the work-item API.
 */
/**
 * The part fields a work item carries. They live here, with the other field sets, so the launch entry reads
 * them without `PartsApi` (and the view engine's part queries that file reaches) coming with them (T-0770).
 */
export const WORK_ITEM_PART_FIELDS = `
  partId handle name status own inherited inheritedFromItemId inheritedFrom implied impliedByPartId
`;

export const USER_FIELDS = `
  id
  name
  email
`;

/**
 * A person who acted (`ActorUser`). No email: the server answers it only for the reader, and another
 * person's address comes from the directory (`workItemUsers`), by id (I-0691).
 */
export const ACTOR_USER_FIELDS = `
  id
  name
`;

export const USER_AGENT_FIELDS = `
  id
  ownerUserId
  key
  displayName
  kind
  status
  lastSeenAt
  ownerUser { ${ACTOR_USER_FIELDS} }
`;

export const WATCHER_FIELDS = `
  actorType
  userId
  agentId
  displayNameSnapshot
  user { ${ACTOR_USER_FIELDS} }
  agent { ${USER_AGENT_FIELDS} }
`;

export const ITEM_TYPE_FIELDS = `
  id
  key
  name
  idPrefix
  icon
  color
  position
  isSystem
  workKind
  workAreaId
`;

export const STATE_FIELDS = `
  id
  key
  statusKey
  name
  group
  color
  isTerminal
  position
`;

export const RELEASE_FIELDS = `
  id
  projectId: workAreaId
  version
  buildNumber
  name
  date
  expectedDate
  status
  focus
  kind
  commitSha
  commitShaVerified
  recordedCommitSha
  commitCorrection {
    id
    oldSha
    newSha
    evidenceKind
    evidence
    reason
    oldShaUnresolvableClaimed
    actorType
    actorDisplayName
    createdAt
  }
  builds
  notes
  itemCount
  exactCount
  createdAt
  updatedAt
`;

export const WORK_ITEM_FIELDS = `
  id
  projectId: workAreaId
  key
  seq
  title
  content
  fields
  source
  legacySource
  parentId
  workflowStateId
  priority
  estimate
  assigneeId
  responsibleActorType
  responsibleUserId
  responsibleAgentId
  responsibleDisplayName
  authorId
  resolvedActorType
  resolvedUserId
  resolvedAgentId
  resolvedAgentSessionId
  resolvedAgentSessionRef
  resolvedDisplayName
  resolvedAt
  closedActorType
  closedUserId
  closedAgentId
  closedAgentSessionId
  closedAgentSessionRef
  closedDisplayName
  closedAt
  iqaClosure
  verifiedSurface
  verificationEvidence
  verifiedAgainstDeployment
  evidenceLink
  evidenceAttachmentId
  confirmedBy
  reopens
  targetDate
  fixedInReleaseId
  foundInReleaseId
  targetReleaseId
  fixedInReleaseDerivation
  foundInReleaseDerivation
  fixedInReleaseEvidence
  foundInReleaseEvidence
  archivedAt
  position
  createdAt
  updatedAt
  lastActivityAt
  itemType { ${ITEM_TYPE_FIELDS} }
  assignee { ${USER_FIELDS} }
  responsible {
    actorType
    userId
    agentId
    displayNameSnapshot
    subagentName
    user { ${ACTOR_USER_FIELDS} }
    agent { ${USER_AGENT_FIELDS} }
  }
  reporter {
    actorType
    userId
    agentId
    agentSessionId
    agentSessionRef
    externalIdentityId
    externalSourceKey
    externalSubjectId
    externalStatus
    externalTrustSourceStatus
    externalTrustSourceConfirmed
    externalProvenance
    externalDetails
    displayNameSnapshot
    user { ${ACTOR_USER_FIELDS} guestRole }
    agent { ${USER_AGENT_FIELDS} }
  }
  resolvedBy {
    actorType
    userId
    agentId
    agentSessionId
    agentSessionRef
    displayNameSnapshot
    user { ${ACTOR_USER_FIELDS} }
    agent { ${USER_AGENT_FIELDS} }
    mergedIntoAgent { id key displayName asSubagent }
  }
  closedBy {
    actorType
    userId
    agentId
    agentSessionId
    agentSessionRef
    displayNameSnapshot
    user { ${ACTOR_USER_FIELDS} }
    agent { ${USER_AGENT_FIELDS} }
    mergedIntoAgent { id key displayName asSubagent }
  }
  state { ${STATE_FIELDS} }
`;

/**
 * What `workItemsPage`/`workItemsByIds` send — the query the list page and every saved view built
 * on it actually share.
 *
 * `resolvedBy`/`closedBy` carry everything the display rule reads — the session ref, the live user
 * and the agent — because the list's Resolved by and Closed by columns render them through
 * `actorBylineWithVerification` (I-0581 AC-5). With only the snapshot, an unverified identity would
 * read as a verified agent or as the person.
 */
/** F-0541: `WorkItem.holder`, asked by the list and the item page. */
export const HOLDER_FIELDS = 'holder { state lastActivityAt staleAfterHours }';

export const WORK_ITEM_LIST_FIELDS = `
  id
  projectId: workAreaId
  key
  seq
  title
  source
  parentId
  workflowStateId
  priority
  estimate
  assigneeId
  responsibleActorType
  responsibleUserId
  responsibleAgentId
  responsibleDisplayName
  ${HOLDER_FIELDS}
  targetDate
  targetReleaseId
  position
  createdAt
  updatedAt
  lastActivityAt
  resolvedDisplayName
  resolvedAt
  closedDisplayName
  closedAt
  askerCount
  childCount
  itemType { ${ITEM_TYPE_FIELDS} }
  assignee { ${USER_FIELDS} }
  responsible {
    actorType
    userId
    agentId
    agentSessionId
    agentSessionRef
    displayNameSnapshot
    subagentName
    user { ${ACTOR_USER_FIELDS} }
    agent { ${USER_AGENT_FIELDS} }
  }
  reporter {
    actorType
    userId
    agentId
    agentSessionId
    agentSessionRef
    externalIdentityId
    externalSourceKey
    externalSubjectId
    externalStatus
    externalTrustSourceStatus
    externalTrustSourceConfirmed
    externalProvenance
    externalDetails
    displayNameSnapshot
    user { ${ACTOR_USER_FIELDS} guestRole }
    agent { ${USER_AGENT_FIELDS} }
  }
  resolvedBy {
    actorType
    userId
    agentId
    agentSessionRef
    displayNameSnapshot
    user { ${ACTOR_USER_FIELDS} }
    agent { ${USER_AGENT_FIELDS} }
    mergedIntoAgent { id key displayName asSubagent }
  }
  closedBy {
    actorType
    userId
    agentId
    agentSessionRef
    displayNameSnapshot
    user { ${ACTOR_USER_FIELDS} }
    agent { ${USER_AGENT_FIELDS} }
    mergedIntoAgent { id key displayName asSubagent }
  }
  state { ${STATE_FIELDS} }
`;

/**
 * Shallow shape for children and relation endpoints. Carries the sort keys
 * (`priority`, `targetDate`, `position`, `createdAt`) so those sub-lists can be
 * ordered by the same rule as the main list.
 */
export const WORK_ITEM_REF_FIELDS = `
  id
  workAreaId
  key
  title
  priority
  targetDate
  position
  createdAt
  responsible {
    actorType
    userId
    agentId
    displayNameSnapshot
    subagentName
    user { ${ACTOR_USER_FIELDS} }
    agent { ${USER_AGENT_FIELDS} }
  }
  reporter {
    actorType
    userId
    agentId
    agentSessionId
    agentSessionRef
    displayNameSnapshot
    user { ${ACTOR_USER_FIELDS} guestRole }
    agent { ${USER_AGENT_FIELDS} }
  }
`;

/**
 * One end of a relation: the ref, plus what decides whether a `blocks` edge still holds (I-0473,
 * DS-56) — the end's type and status, and the withdrawal when it is a withdrawn decision.
 */
export const RELATION_END_FIELDS = `
  ${WORK_ITEM_REF_FIELDS}
  itemType { key }
  state { ${STATE_FIELDS} }
  decisionWithdrawal { note withdrawnAt withdrawnBy { displayName } }
`;

/**
 * A relation's source end, which is the blocker of an inbound `blocks` edge: also its own
 * `implements` edges, so a resolved blocker an open item still implements keeps holding (I-1105,
 * `blockerEdgeStanding`) — the same reading `featit plan` and `featit show` make.
 */
export const RELATION_SOURCE_FIELDS = `
  ${RELATION_END_FIELDS}
  relations { type sourceId targetId redacted source { key title state { statusKey isTerminal } } }
`;

/**
 * Every reference in the item's description, resolved by the server (I-0537).
 *
 * ⛔ Selected on the **detail** query, alongside `content`, and deliberately not fetched separately:
 * the description and the expansions of its shorthand have to arrive in one payload or the context
 * paints with its shorthand showing and resolves a moment later, which is what DR-5 forbids.
 */
export const CONTENT_REFERENCE_FIELDS = `
  contentReferences {
    token
    kind
    item { id key title state itemTypeKey workAreaId }
    matches { heading file excerpt }
    unresolvedNote
  }
`;

/**
 * Detail view additionally resolves the tree children, typed relations, the parent and the watcher list,
 * and its type's policies: what the item page offers from them (I-2587: `makes`, Make the Job) is read
 * there, never on a list row, so only this read carries them.
 */
export const WORK_ITEM_DETAIL_FIELDS = `
  ${WORK_ITEM_FIELDS}
  ${HOLDER_FIELDS}
  itemType { policies }
  serviceLevel {
    rule state startedAt spentMinutes pausedIn
    goal { minutes dueAt remainingMinutes met }
    deadline { minutes dueAt remainingMinutes met }
  }
  ideaWait { state since expectedBy endedAt line twiceLateFrom askNow askedAt askedName askNowRefusal }
  ideaOwner { reason line }
  ${CONTENT_REFERENCE_FIELDS}
  formerKeys
  descriptionVersionCount
  filing
  askerCount
  askers { kind ref displayName }
  suggestedDuplicates { id key title state { key name group } itemType { key name } }
  parent { id workAreaId workAreaName key title archivedAt }
  parts { ${WORK_ITEM_PART_FIELDS} }
  partsMode
  watchers { ${WATCHER_FIELDS} }
  children {
    ${WORK_ITEM_REF_FIELDS}
    state { ${STATE_FIELDS} }
  }
  relations {
    id
    sourceId
    targetId
    type
    redacted
    source { ${RELATION_SOURCE_FIELDS} }
    target { ${RELATION_END_FIELDS} }
  }
`;

export const WORK_ITEM_EVENT_FIELDS = `
  type
  projectId: workAreaId
  revision
  at
  workItem { ${WORK_ITEM_FIELDS} }
  relation {
    id
    sourceId
    targetId
    type
    redacted
    source { ${RELATION_SOURCE_FIELDS} }
    target { ${RELATION_END_FIELDS} }
  }
`;

export const WORK_ITEM_ACTIVITY_FIELDS = `
  id
  source
  sourceId
  type
  revision
  at
  itemId
  itemKey
  itemTitle
  responsible {
    key
    label
    actorType
    userId
    agentId
  }
  actor {
    actorType
    userId
    agentId
    agentSessionRef
    displayName
    mergedIntoAgent { id key displayName asSubagent }
  }
  summary
`;
